import { _assert } from '@naturalcycles/js-lib'
import { Goal } from '@naturalcycles/shared'
import { ncNavigate } from './nc-navigate'
import { getTrafficOrigin } from './trafficOriginUtil'

/**
 * Gets the environment-specific signup URL with an optional
 * path appended to it.
 *
 * @example
 * ```ts
 * const { locale } = useContext(languageContext)
 *
 * const manageAccountUrl = getSignupURL(
 *   `${locale}/account/login`
 * ).toString()
 *
 * const baseUrl = getSignupURL().toString()
 *
 * ncNavigate(shouldLogin ? manageAccountUrl : baseUrl)
 */
export function getSignupURL(path?: string): URL {
  const gatsbySignupUrl = process.env['GATSBY_SIGNUP_URL']
  _assert(gatsbySignupUrl, 'GATSBY_SIGNUP_URL is not defined')

  let fullUrl = new URL(gatsbySignupUrl)
  if (path) {
    fullUrl = new URL(path, fullUrl)
  }

  return fullUrl
}

export function addUTMParamsToUrl(url: URL, customSource: string): void {
  const origin = getTrafficOrigin()
  url.searchParams.set('utm_source_web_medium', origin.utm_medium)
  if (customSource) url.searchParams.set('utm_source_web', customSource)
}

// eslint-disable-next-line
export function enhanceSignupUrl(
  url: URL,
  goal: Goal | undefined,
  codeReferral: string | null,
  codeName: string | null,
  ignoreConfirmation: boolean | undefined,
  code?: string,
): string {
  const windowLoaded = typeof window !== 'undefined'
  const storedReferral = windowLoaded && window.sessionStorage.getItem('discountReferral')
  const referral = codeReferral || storedReferral

  const storedName = windowLoaded && window.sessionStorage.getItem('discountName')
  const name = codeName || storedName

  if (typeof code === 'string' && code.length > 0) {
    url.pathname += '/secret'
    url.searchParams.set('code', code)

    // If the user has arrived with a referral id we pass that on to signup
    // Otherwise if they come from an influencer or referral we set
    // ignoreconfirmation to be true
    const fromReferral = typeof referral === 'string' && referral.length > 1
    const fromInfluencer = typeof name === 'string' && name.length > 1

    if (fromReferral) {
      url.searchParams.set('referral', referral)
    }

    // We only set an `ignoreConfirmation` searchParam for /signup/secret
    // links, referrals and influencer page traffic to prevent them from
    // seeing the confirmation page in signup
    if (fromReferral || fromInfluencer) {
      url.searchParams.set('ignoreConfirmation', 'true')
    } else if (typeof ignoreConfirmation === 'boolean') {
      url.searchParams.set('ignoreConfirmation', ignoreConfirmation.toString())
    }
  }

  // for users from the pricing page who have selected their plan
  // and will be taken directly to corresponding payment page
  if (goal) {
    url.searchParams.set('mode', goal.toString())
  }

  return url.toString()
}

export function addQueryStringAndNavigate(href: string, ncUtmSetSource?: string): boolean {
  const clickUrl = new URL(href)
  if (window.Cookiebot?.hasResponse) {
    clickUrl.searchParams.set('cp', String(window.Cookiebot?.consent?.preferences))
    clickUrl.searchParams.set('cs', String(window.Cookiebot?.consent?.statistics))
    clickUrl.searchParams.set('cm', String(window.Cookiebot?.consent?.marketing))
  }

  addUTMParamsToUrl(clickUrl, ncUtmSetSource || 'signup_button')
  ncNavigate(clickUrl.toString())
  return false
}
